import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { User } from "lucide-react";
import { MAIN_URL } from "../../Configurations/Urls";
import ProfileImagePreview from "./ProfileImagePreview";

/**
 * @param {unknown} raw
 * @param {{ folder?: "employees" | "persons" | "interns" | "students" }} [opts]
 */
export function buildListProfileImageUrl(raw, opts = {}) {
  const folder = opts.folder || "employees";
  if (raw == null) return "";
  let t = String(raw).trim();
  if (!t || t === "-" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") {
    return "";
  }

  // Legacy symlink path used on some EMS deployments.
  t = t.replace(/\/storage\/students\//gi, "/storage/app/public/students/");

  if (t.startsWith("http://") || t.startsWith("https://")) {
    try {
      const parsed = new URL(t);
      // Backend asset() sometimes emits localhost while the SPA hits the live API.
      if (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost") {
        return `${MAIN_URL}${parsed.pathname}${parsed.search}`;
      }
    } catch {
      // keep original absolute URL
    }
    return t;
  }
  if (t.startsWith("/storage/app/public")) return `${MAIN_URL}${t}`;
  if (t.startsWith("/storage/")) return `${MAIN_URL}${t}`;
  if (t.startsWith("storage/")) return `${MAIN_URL}/${t}`;
  if (
    t.startsWith("employees/") ||
    t.startsWith("persons/") ||
    t.startsWith("interns/") ||
    t.startsWith("students/")
  ) {
    return `${MAIN_URL}/storage/app/public/${t}`;
  }
  return `${MAIN_URL}/storage/app/public/${folder}/${t}`;
}

const PROFILE_IMAGE_KEYS = [
  "profile_image_url",
  "photo_url",
  "image_url",
  "avatar_url",
  "person.profile_image_url",
  "organization_person.person.profile_image_url",
];

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
}

/** Person nested under organization_person / organizationPerson (API casing varies). */
function resolveNestedPerson(orgPerson) {
  if (!orgPerson || typeof orgPerson !== "object") return null;
  return firstDefined(orgPerson.person, orgPerson.Person) || null;
}

/** Person / org-person tree on a trainer (or organization user) payload. */
function resolvePersonFromTrainer(trainer) {
  if (!trainer || typeof trainer !== "object") return null;
  const orgPerson = firstDefined(
    trainer.organization_person,
    trainer.organizationPerson
  );
  return (
    firstDefined(trainer.person, trainer.Person) ||
    resolveNestedPerson(orgPerson) ||
    null
  );
}

export function resolveRowProfileImageUrl(row, getNestedValue) {
  if (!row) return "";
  for (const key of PROFILE_IMAGE_KEYS) {
    const val = getNestedValue ? getNestedValue(row, key) : row[key];
    const url = buildListProfileImageUrl(val);
    if (url) return url;
  }
  const person = resolvePersonFromTrainer(row) || resolveNestedPerson(row.organization_person || row.organizationPerson);
  const fromPerson = buildListProfileImageUrl(person?.profile_image_url);
  if (fromPerson) return fromPerson;
  return "";
}

/** Logged-in user (top bar) profile photo from session payload. */
export function resolveUserProfileImageUrl(userData) {
  if (!userData) return "";
  const loginuser = Array.isArray(userData.loginuser)
    ? userData.loginuser[0]
    : userData.loginuser;
  const isStudent = Boolean(
    loginuser?.organization_ems_student_id ||
      userData?.organization_ems_student_id
  );
  const folder = isStudent ? "students" : "employees";

  const orgPerson =
    loginuser?.organization_person ||
    loginuser?.organizationPerson ||
    loginuser?.org_persons ||
    loginuser?.OrgPersons;
  const nestedPerson =
    orgPerson?.person ||
    orgPerson?.Person ||
    loginuser?.person ||
    loginuser?.Person;

  // Student portal: prefer organization_ems_students.profile_image_url on loginuser.
  // Employee / shared resource / intern: prefer person photo, then employee/intern column.
  const candidates = isStudent
    ? [
        loginuser?.profile_image_url,
        userData.profile_image_url,
        loginuser?.profile_image_file,
      ]
    : [
        userData.profile_image_url,
        nestedPerson?.profile_image_url,
        loginuser?.profile_image_url,
        userData.person?.profile_image_url,
        userData.Person?.profile_image_url,
        loginuser?.organization_person?.person?.profile_image_url,
        loginuser?.organization_person?.Person?.profile_image_url,
        loginuser?.organizationPerson?.person?.profile_image_url,
        loginuser?.organizationPerson?.Person?.profile_image_url,
        loginuser?.org_persons?.person?.profile_image_url,
        loginuser?.org_persons?.Person?.profile_image_url,
        loginuser?.OrgPersons?.person?.profile_image_url,
        loginuser?.OrgPersons?.Person?.profile_image_url,
        loginuser?.person?.profile_image_url,
        loginuser?.Person?.profile_image_url,
      ];

  for (const raw of candidates) {
    const url = buildListProfileImageUrl(raw, { folder });
    if (url) return url;
  }
  return "";
}

/** Batch trainer row from dashboard APIs — prefer trainer.person photo. */
export function resolveTrainerProfileImageFromRow(item) {
  if (!item) return "";
  const trainer = item.trainer || item;
  const person = resolvePersonFromTrainer(trainer);
  const candidates = [
    // Primary: persons.profile_image_url via trainer.person / organization_person.person
    person?.profile_image_url,
    person?.photo_url,
    person?.image_url,
    trainer?.person?.profile_image_url,
    trainer?.Person?.profile_image_url,
    trainer?.organization_person?.person?.profile_image_url,
    trainer?.organization_person?.Person?.profile_image_url,
    trainer?.organizationPerson?.person?.profile_image_url,
    trainer?.organizationPerson?.Person?.profile_image_url,
    // Backend may attach flattened URL on trainer
    trainer?.profile_image_url,
    item?.person?.profile_image_url,
    item?.Person?.profile_image_url,
  ];
  for (const raw of candidates) {
    const url = buildListProfileImageUrl(raw);
    if (url) return url;
  }
  return "";
}

/** Collect person photos from any batch-class rows that already include them. */
export function buildTrainerPersonPhotoIndex(rows, getTrainerName, normalizeName) {
  const byOrgUserId = new Map();
  const byAppUserId = new Map();
  const byName = new Map();

  (rows || []).forEach((row) => {
    const url = resolveTrainerProfileImageFromRow(row);
    if (!url) return;

    const teid =
      row?.trainer_employee_id ?? row?.trainer?.organization_user_id ?? null;
    if (teid != null && String(teid).trim() !== "") {
      byOrgUserId.set(String(teid), url);
    }

    const appUserId =
      row?.trainer?.application_user_id ??
      row?.trainer?.applicationuser?.[0]?.application_user_id ??
      null;
    if (appUserId != null && String(appUserId).trim() !== "") {
      byAppUserId.set(String(appUserId), url);
    }

    const nameKey = normalizeName(getTrainerName(row));
    if (nameKey && nameKey !== "trainer") {
      byName.set(nameKey, url);
    }
  });

  return { byOrgUserId, byAppUserId, byName };
}

function normalizeTrainerNameKey(value) {
  return String(value ?? "")
    .replace(/[\n\r\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Same photo index the EMS dashboard uses: employee Person URLs keyed by
 * organization user, application user (shared resource), and name.
 */
export function buildEmployeePhotoLookup(employees) {
  const byOrgUserId = new Map();
  const byAppUserId = new Map();
  const byName = new Map();
  (employees || []).forEach((emp) => {
    const url = buildListProfileImageUrl(
      emp?.profile_image_url ||
        emp?.org_persons?.person?.profile_image_url ||
        emp?.OrgPersons?.Person?.profile_image_url ||
        emp?.org_persons?.Person?.profile_image_url ||
        ""
    );
    if (!url) return;

    const orgUserId =
      emp?.organization_user_id ||
      emp?.org_persons?.organization_user_id ||
      emp?.OrgPersons?.organization_user_id;
    if (orgUserId != null && String(orgUserId).trim() !== "") {
      byOrgUserId.set(String(orgUserId), url);
    }

    const appUserId =
      emp?.application_user_id ||
      emp?.org_persons?.organization_user?.application_user_id ||
      emp?.OrgPersons?.OrganizationUser?.application_user_id;
    if (appUserId != null && String(appUserId).trim() !== "") {
      byAppUserId.set(String(appUserId), url);
    }

    const nameKey = normalizeTrainerNameKey(
      emp?.Name ||
        emp?.full_name ||
        emp?.name ||
        [emp?.first_name, emp?.middle_name, emp?.last_name].filter(Boolean).join(" ")
    );
    if (nameKey) byName.set(nameKey, url);

    const first = String(emp?.first_name || "").trim();
    const last = String(emp?.last_name || "").trim();
    if (first && last) {
      byName.set(normalizeTrainerNameKey(`${first} ${last}`), url);
    }
  });
  return { byOrgUserId, byAppUserId, byName };
}

/** Dashboard trainer photo: employee match first, then the photo already on the row. */
export function resolveEmployeeTrainerPhoto(row, lookup) {
  if (!row || !lookup) return buildListProfileImageUrl(row?.profile_image_url || "");

  const appUserId =
    row?.trainer_application_user_id ??
    row?.application_user_id ??
    row?.trainer?.application_user_id ??
    "";
  const orgUserId =
    row?.trainer_employee_id ??
    row?.organization_user_id ??
    row?.trainer?.organization_user_id ??
    "";
  const nameKey = normalizeTrainerNameKey(
    row?.trainer_card_name || row?.trainer || row?.name || ""
  );

  if (appUserId != null && String(appUserId).trim() !== "") {
    const byApp = lookup.byAppUserId.get(String(appUserId));
    if (byApp) return byApp;
  }
  if (orgUserId != null && String(orgUserId).trim() !== "") {
    const byId = lookup.byOrgUserId.get(String(orgUserId));
    if (byId) return byId;
  }
  if (nameKey && nameKey !== "trainer" && nameKey !== "-") {
    const byName = lookup.byName.get(nameKey);
    if (byName) return byName;
    const parts = nameKey.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      const fuzzy = lookup.byName.get(`${parts[0]} ${parts[parts.length - 1]}`);
      if (fuzzy) return fuzzy;
    }
  }

  return buildListProfileImageUrl(row?.profile_image_url || "");
}

export function applyEmployeeTrainerPhotos(rows, lookup) {
  if (!lookup || !Array.isArray(rows)) return rows;
  return rows.map((row) => {
    const photo = resolveEmployeeTrainerPhoto(row, lookup);
    if (!photo || photo === row?.profile_image_url) return row;
    return { ...row, profile_image_url: photo };
  });
}

/** Matches `.ems-avatar` / `.ems-t-avatar` fallback styling. */
const AVATAR_FALLBACK_BG = "linear-gradient(140deg, #3f4756, #6b7686)";
const AVATAR_FALLBACK_COLOR = "#ffffff";

/**
 * Circular list avatar: profile photo when available, otherwise a people icon.
 * Click expands a larger photo preview when an image URL is available.
 */
export default function ListPersonAvatar({
  row,
  getNestedValue,
  imageUrl: imageUrlProp,
  size = 34,
  sx = {},
  previewable = true,
  previewAlt = "Profile photo",
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const imageUrl =
    imageUrlProp ||
    (row ? resolveRowProfileImageUrl(row, getNestedValue) : "");

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  const canPreview = Boolean(previewable && imageUrl && !imageFailed);

  const openPreview = (e) => {
    if (!canPreview) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setPreviewOpen(true);
  };

  const shellSx = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    borderRadius: "50%",
    flexShrink: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    background: AVATAR_FALLBACK_BG,
    color: AVATAR_FALLBACK_COLOR,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    ...(canPreview
      ? {
          cursor: "zoom-in",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            transform: "scale(1.06)",
            boxShadow: "0 0 0 2px rgba(13, 148, 136, 0.35)",
          },
        }
      : {}),
    ...sx,
  };

  return (
    <>
      {imageUrl && !imageFailed ? (
        <Box
          component="img"
          src={imageUrl}
          alt={previewAlt}
          title={canPreview ? "Click to preview photo" : undefined}
          onError={() => setImageFailed(true)}
          onClick={openPreview}
          role={canPreview ? "button" : undefined}
          tabIndex={canPreview ? 0 : undefined}
          onKeyDown={
            canPreview
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") openPreview(e);
                }
              : undefined
          }
          sx={{
            ...shellSx,
            objectFit: "cover",
            background: AVATAR_FALLBACK_BG,
          }}
        />
      ) : (
        <Box sx={shellSx} aria-hidden>
          <User size={Math.max(14, Math.round(size * 0.48))} strokeWidth={2.25} />
        </Box>
      )}

      <ProfileImagePreview
        open={previewOpen}
        src={imageUrl}
        alt={previewAlt}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}
